/**
 * The privacy boundary. Everything the diagnostics feature sends passes through here, and
 * this file names every field explicitly.
 *
 * ALLOW-LIST, NEVER A DENY-LIST. A raw RTCStatsReport contains the user's LAN IP, their
 * public IP and the TURN server's address (on `local-candidate` / `remote-candidate` /
 * `candidate-pair`), plus device labels and `deviceId`, a stable per-profile fingerprint.
 * Nothing is copied wholesale: a field not listed below does not leave the browser.
 *
 * Deliberately not collected:
 *   - any IP address (candidateType/protocol are kept, never the address)
 *   - deviceId, device labels
 *   - audioLevel: a per-second series reveals who spoke and when. Only whether the mic
 *     produced packets is kept, which answers "was my mic dead" without exposing the
 *     conversation.
 *
 * A field an engine cannot report is left undefined (which the server stores as null),
 * never defaulted to 0.
 */

function detectEngine () {
  const ua = navigator.userAgent
  // Order matters: Chrome/Edge UA strings also contain "Safari".
  if (/Firefox\/|FxiOS/.test(ua)) return 'Gecko'
  if (/Edg\/|Chrome\/|CriOS/.test(ua)) return 'Blink'
  if (/Safari\//.test(ua)) return 'WebKit'
  return 'unknown'
}

function browserName (ua) {
  let m
  if ((m = ua.match(/Edg\/([\d.]+)/))) return 'Edge ' + m[1].split('.')[0]
  if ((m = ua.match(/Firefox\/([\d.]+)/))) return 'Firefox ' + m[1].split('.')[0]
  if ((m = ua.match(/Chrome\/([\d.]+)/))) return 'Chrome ' + m[1].split('.')[0]
  if ((m = ua.match(/Version\/([\d.]+).*Safari/))) return 'Safari ' + m[1].split('.')[0]
  return 'Unknown'
}

function osName (ua) {
  if (/Windows NT 10/.test(ua)) return 'Windows 10/11'
  if (/Windows/.test(ua)) return 'Windows'
  if (/iPhone|iPad|iPod/.test(ua)) return 'iOS'
  if (/Android/.test(ua)) return 'Android'
  if (/Mac OS X/.test(ua)) return 'macOS'
  if (/Linux/.test(ua)) return 'Linux'
  return 'Unknown'
}

function deviceKind (ua) {
  if (/iPhone|iPod/.test(ua)) return 'iPhone'
  if (/iPad/.test(ua)) return 'iPad'
  if (/Android/.test(ua)) return /Mobile/.test(ua) ? 'Android phone' : 'Android tablet'
  return 'Desktop'
}

/**
 * Static client context, sent once on the first flush.
 *
 * `cores` and `memory` are approximations, not readings: browsers quantise and clamp both
 * to resist fingerprinting, so they disagree with each other and with the machine. They
 * are a rough "is this a weak machine" signal only. No diagnosis rule reads them, and the
 * UI has to say so.
 */
export function clientContext (extra) {
  const ua = navigator.userAgent
  return {
    engine: detectEngine(),
    browser: browserName(ua),
    os: osName(ua),
    device: deviceKind(ua),
    cores: navigator.hardwareConcurrency || undefined,
    // Chromium-only. undefined -> stored as null, meaning "not reported", not "0 GB".
    memory: navigator.deviceMemory === undefined ? null : navigator.deviceMemory,
    effects: !!(extra && extra.effects),
    app_version: window.version || undefined,
    sdk_version: (extra && extra.sdkVersion) || undefined
  }
}

/**
 * Counter deltas. WebRTC stats are cumulative-since-connection, so every rate and
 * every "how much happened in this bucket" figure is a difference of two readings.
 */
export class Deltas {
  constructor () {
    this.prev = {}
  }

  /**
   * Increase in `value` since the last call for this key. undefined on the first call and
   * on a counter reset (a republish restarts the counters): a reset says nothing about the
   * interval, and 0 would read as "no freezes, no loss" during the reconnect that caused it.
   */
  delta (key, value) {
    if (typeof value !== 'number' || !isFinite(value)) return undefined
    const p = this.prev[key]
    this.prev[key] = value
    if (p === undefined) return undefined
    const d = value - p
    return d >= 0 ? d : undefined
  }

  /** kbps between two byte readings. */
  kbps (key, bytes, tsMs) {
    if (typeof bytes !== 'number' || typeof tsMs !== 'number') return undefined
    const p = this.prev['_b' + key]
    this.prev['_b' + key] = { bytes, ts: tsMs }
    if (!p || tsMs <= p.ts) return undefined
    // A counter reset (republish keeps the rid, the encoder restarts at 0) reads as
    // negative. Unknown, not zero: a reset says nothing about the bitrate.
    if (bytes < p.bytes) return undefined
    return Math.round(((bytes - p.bytes) * 8) / (tsMs - p.ts))
  }
}

const pct = (part, whole) => (whole > 0 ? +((part / whole) * 100).toFixed(2) : 0)

/**
 * Map one sender report (local track) -> the allow-listed outbound shape.
 * `d` is shared across all tracks and peers; keys must stay namespaced by kind/ssrc.
 */
export function mapOutbound (report, d, kind) {
  const out = {}
  let ice

  report.forEach((s) => {
    if (s.type === 'outbound-rtp') {
      // Simulcast: several outbound-rtp entries share a report (one per rid). Sum the
      // bitrates and take the top layer's geometry — that is what the SFU could forward.
      const key = kind + '_' + (s.rid || s.ssrc)
      const kb = d.kbps(key, s.bytesSent, s.timestamp)
      if (kb !== undefined) {
        if (kind === 'video') out.vkbps = (out.vkbps || 0) + kb
        else out.akbps = (out.akbps || 0) + kb
      }
      if (kind === 'video') {
        if (typeof s.framesPerSecond === 'number') out.fps = Math.max(out.fps || 0, Math.round(s.framesPerSecond))
        if (s.frameWidth && (!out.w || s.frameWidth > out.w)) {
          out.w = s.frameWidth
          out.h = s.frameHeight
        }
        // Per-frame encode time, ms. Cumulative like the rest; delta both and divide.
        // One term of the end-to-end latency estimate; absent on engines that omit it.
        const ef = d.delta(key + '_ef', s.framesEncoded)
        const et = d.delta(key + '_et', s.totalEncodeTime)
        if (ef !== undefined && et !== undefined && ef > 0) {
          out.enc = Math.max(out.enc || 0, Math.round((et / ef) * 1000))
        }
        // Gecko does not implement qualityLimitationDurations at all -> stays undefined.
        // Cumulative seconds per cause; the per-bucket delta is what is stored.
        if (s.qualityLimitationDurations) {
          const q = s.qualityLimitationDurations
          const cpu = d.delta(key + '_qcpu', q.cpu)
          const bw = d.delta(key + '_qbw', q.bandwidth)
          const none = d.delta(key + '_qnone', q.none)
          if (cpu !== undefined || bw !== undefined || none !== undefined) {
            // Each simulcast layer carries its own durations, and within a layer they
            // sum to the elapsed time. Keep the worst-limited layer's whole breakdown:
            // a per-cause max across layers double-counts, since layer A's `bandwidth`
            // and layer B's `none` both survive and a 2s poll could report 4s of
            // limitation.
            const layer = { cpu: cpu || 0, bandwidth: bw || 0, none: none || 0 }
            const prev = out.qlim
            const limited = (x) => x.cpu + x.bandwidth
            if (!prev || limited(layer) > limited(prev)) {
              out.qlim = {
                cpu: +layer.cpu.toFixed(2),
                bandwidth: +layer.bandwidth.toFixed(2),
                none: +layer.none.toFixed(2)
              }
            }
          }
        }
      }
    }

    // Uplink loss + RTT as the SFU sees us, via RTCP receiver reports.
    if (s.type === 'remote-inbound-rtp') {
      if (typeof s.roundTripTime === 'number') out.rtt = Math.round(s.roundTripTime * 1000)
      // Worst layer wins: simulcast reports one entry per ssrc.
      if (typeof s.fractionLost === 'number') {
        const loss = +(s.fractionLost * 100).toFixed(2)
        if (out.loss_out === undefined || loss > out.loss_out) out.loss_out = loss
      }
    }

    if (s.type === 'candidate-pair' && (s.nominated || s.state === 'succeeded')) {
      // Gecko does not implement availableOutgoingBitrate -> stays undefined.
      if (typeof s.availableOutgoingBitrate === 'number') {
        out.head = Math.round(s.availableOutgoingBitrate / 1000)
      }
      if (typeof s.currentRoundTripTime === 'number' && out.rtt === undefined) {
        out.rtt = Math.round(s.currentRoundTripTime * 1000)
      }
    }

    // Candidate TYPE and network medium only. The address is never read.
    if (s.type === 'local-candidate') {
      if (s.candidateType) ice = s.candidateType
      if (s.networkType) out._net = s.networkType   // Blink-only; stripped by the collector
    }
  })

  return { out, ice }
}

/**
 * Map one receiver report (a remote track) -> the allow-listed inbound shape.
 *
 * `d` is shared across all tracks and peers; keys must stay namespaced by ref/kind/ssrc.
 *
 * @param settling  the remote publication's impairment metrics cannot be trusted: it is
 *   muted, or inside the start/unmute grace window.
 *
 * Such a track still produces stats, and they look exactly like a fault: frames stop
 * arriving so freezeCount climbs, audio stops so concealedSamples climbs. While settling
 * the impairment metrics are dropped, but their deltas are still taken: Deltas is a running
 * difference, so skipping the call would leave `prev` behind and dump the whole muted
 * period's backlog into the first bucket after the unmute.
 */
export function mapInbound (report, d, kind, ref, settling) {
  const inb = { ref }

  report.forEach((s) => {
    if (s.type !== 'inbound-rtp') return

    const key = ref + '_' + kind + '_' + s.ssrc
    const kb = d.kbps(key, s.bytesReceived, s.timestamp)
    if (kb !== undefined) {
      if (kind === 'video') inb.vkbps = kb
      else inb.akbps = kb
    }

    const lost = d.delta(key + '_lost', s.packetsLost)
    const recv = d.delta(key + '_recv', s.packetsReceived)
    if (lost !== undefined && recv !== undefined) {
      inb.loss = pct(lost, lost + recv)
    }

    if (kind === 'video') {
      if (typeof s.framesPerSecond === 'number') inb.fps = Math.round(s.framesPerSecond)
      if (s.frameWidth) {
        inb.w = s.frameWidth
        inb.h = s.frameHeight
      }
      // Per-frame decode time, ms. One term of the end-to-end latency estimate.
      const df = d.delta(key + '_df', s.framesDecoded)
      const dt = d.delta(key + '_dt', s.totalDecodeTime)
      if (df !== undefined && dt !== undefined && df > 0) {
        inb.dec = Math.round((dt / df) * 1000)
      }
      // Maps 1:1 to "the video froze", and is populated on all three engines. Taken even
      // while settling so prev stays current, but not reported: a camera that is off, or
      // one that only just started, is not a frozen camera.
      const fz = d.delta(key + '_fz', s.freezeCount)
      const fzd = d.delta(key + '_fzd', s.totalFreezesDuration)
      if (!settling) {
        if (fz !== undefined) inb.freeze = fz
        if (fzd !== undefined) inb.freeze_dur = +fzd.toFixed(2)
      }

      const nack = d.delta(key + '_nack', s.nackCount)
      const pli = d.delta(key + '_pli', s.pliCount)
      if (nack !== undefined) inb.nack = nack
      if (pli !== undefined) inb.pli = pli
    } else {
      // "The audio was choppy / robotic".
      //
      // concealedSamples on its own is not that. Most of it is silence: with DTX the
      // sender stops transmitting when nobody is talking and the decoder fills the gap,
      // which counts as concealment. A quiet call with no audio problem at all still
      // reports a large share of its samples concealed.
      //
      // silentConcealedSamples is the subset that was silence. The difference is the
      // concealment a human would actually hear. All three engines declare it; where it
      // is missing, fall back to the raw figure rather than reporting nothing.
      const conc = d.delta(key + '_conc', s.concealedSamples)
      const silent = d.delta(key + '_sconc', s.silentConcealedSamples)
      const total = d.delta(key + '_tot', s.totalSamplesReceived)
      if (!settling && conc !== undefined && total !== undefined) {
        const audible = silent === undefined ? conc : Math.max(0, conc - silent)
        inb.conceal = pct(audible, total)
      }
    }

    // jitterBufferDelay is cumulative seconds and is meaningless raw; engines differ by
    // orders of magnitude. Normalise by the emitted count to get the average delay per
    // packet in this bucket.
    const jbd = d.delta(key + '_jbd', s.jitterBufferDelay)
    const jbe = d.delta(key + '_jbe', s.jitterBufferEmittedCount)
    if (jbd !== undefined && jbe !== undefined && jbe > 0) {
      inb.jit = Math.round((jbd / jbe) * 1000)
    }
  })

  return inb
}
